import * as React from "react";
import { cn } from "./utils";

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalidMessage?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      invalidMessage,
      id,
      onInput,
      onChange,
      onPointerDownCapture,
      onMouseDownCapture,
      onClickCapture,
      children,
      ...props
    },
    ref,
  ) => {
    const lastInputValue = React.useRef<string | null>(null);

    return (
      <div className="flex w-full flex-col gap-1">
        <select
          id={id}
          ref={ref}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer",
            invalidMessage && "border-red-600 focus-visible:ring-red-600/40",
            className,
          )}
          aria-invalid={!!invalidMessage || undefined}
          aria-describedby={
            invalidMessage ? `${id || "select"}-error` : undefined
          }
          onInput={(e) => {
            onInput?.(e as any);
            if (onChange) {
              lastInputValue.current = e.currentTarget.value;
              onChange(e as any);
            }
          }}
          onChange={(e) => {
            if (
              lastInputValue.current !== null &&
              lastInputValue.current === e.currentTarget.value
            ) {
              lastInputValue.current = null;
              return;
            }
            lastInputValue.current = null;
            onChange?.(e);
          }}
          onPointerDownCapture={(e) => {
            e.stopPropagation();
            onPointerDownCapture?.(e);
          }}
          onMouseDownCapture={(e) => {
            e.stopPropagation();
            onMouseDownCapture?.(e);
          }}
          onClickCapture={(e) => {
            e.stopPropagation();
            onClickCapture?.(e);
          }}
          {...props}
        >
          {children}
        </select>
        {invalidMessage ? (
          <span
            id={`${id || "select"}-error`}
            role="alert"
            className="text-xs text-red-600"
          >
            {invalidMessage}
          </span>
        ) : null}
      </div>
    );
  },
);
Select.displayName = "Select";

export const MultiSelect = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>((props, ref) => <select ref={ref} multiple {...props} />);
MultiSelect.displayName = "MultiSelect";
